import type { HttpClient } from '~/shared/api'
import { ApiService, ErrorApi } from '~/shared/api'
import type { Logger, TokenManager } from '~/shared/interfaces'
import { UserModel, RefreshTokenModel } from '~/models/user'
import { unwrapObject } from '~/shared/utils/response'

const HTTP_UNAUTHORIZED = 401

export interface AuthorizeOptions {
  /** С флагом null означает только 401, сбой связи пробрасывается */
  throwOnTransientError?: boolean
}

export class UserService extends ApiService {
  private refreshPromise: Promise<RefreshTokenModel | null> | null = null

  constructor(
    http: HttpClient,
    private readonly tokenManager: TokenManager,
    private readonly authHost: string,
    private readonly logger: Logger
  ) {
    super(http)
  }

  readonly urls = {
    authorize: '/api/v1/yavagro/users-management/users/sf/authorize',
    refresh: '/api/ext/rshb-auth/refresh',
  }

  async getAuthorizedUser(options: AuthorizeOptions = {}): Promise<UserModel | null> {
    return this.http
      .request({
        url: this.urls.authorize,
        method: 'POST',
        headers: {
          'Authorization-SF-Token': this.tokenManager.getAccessToken() || '',
        },
        silent: options.throwOnTransientError,
      })
      .then(({ data }) => {
        const user = unwrapObject(data)
        return user ? new UserModel(user) : null
      })
      .catch((error: unknown) => {
        // без флага любая ошибка означает "не авторизован"
        if (!options.throwOnTransientError || this.getErrorStatus(error) === HTTP_UNAUTHORIZED) {
          return null
        }

        throw error
      })
  }

  /** Refresh-токен одноразовый, поэтому обновление single-flight */
  getAccessTokenByRefresh(): Promise<RefreshTokenModel | null> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.performRefresh().finally(() => {
        this.refreshPromise = null
      })
    }

    return this.refreshPromise
  }

  private performRefresh(): Promise<RefreshTokenModel | null> {
    let refreshToken = this.tokenManager.getRefreshToken()
    const updateRefreshToken = this.tokenManager.getUpdateRefreshToken()

    if (!refreshToken && updateRefreshToken) {
      this.tokenManager.consumeUpdateRefreshToken(updateRefreshToken)
      refreshToken = updateRefreshToken
    }

    if (!refreshToken) {
      return Promise.resolve(null)
    }

    return this.http
      .request({
        url: `${this.authHost}${this.urls.refresh}`,
        method: 'POST',
        data: { refreshToken },
        silent: true,
      })
      .then(({ data }) => {
        const result = unwrapObject(data, 'result')
        const tokens = result ? new RefreshTokenModel(result) : null

        if (!tokens?.accessToken) {
          this.logger.error('Обновление токенов отклонено: в ответе нет токена')
          return null
        }

        return tokens
      })
      .catch((error: unknown) => {
        if (this.getErrorStatus(error) === HTTP_UNAUTHORIZED) {
          this.logger.warn('SSO отклонил refresh-токен', error)
        } else {
          this.logger.error('Не удалось обновить токены', error)
        }

        return null
      })
  }

  private getErrorStatus(error: unknown): number | null {
    return ErrorApi.isApiError(error) ? error.code : null
  }
}
